import { JUST_SAVED_NAMED_REPORT, RouteNames } from "model/routing";
import { AllowedParams } from "../allowedParams";
import { lazyWithRetry } from "../lazyWithRetry";

export type IRouteProps = {
  path?: string;
  component: any;
  exact?: boolean;
  key?: string;
  allowedParams?: AllowedParams;
};

export const ROUTES: { [key in RouteNames]: IRouteProps } = {
  [RouteNames.analytics]: {
    path: "/analytics",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "AnalyticReportSelector" */
          "routes/Analytics/ReportSelector/ReportSelector"
        )
    ),
  },
  [RouteNames.analyticsReport]: {
    path: "/analytics/report/:campaignId?",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "AnalyticsPage" */
          "routes/Analytics/AnalyticsPage"
        )
    ),
  },
  [RouteNames.analyticsNamedReport]: {
    path: "/analytics/named-report/:reportId/:campaignId?",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "AnalyticsPage" */
          "routes/Analytics/AnalyticsPage"
        )
    ),
  },
  [RouteNames.analyticsJustSavedReport]: {
    path: `/analytics/${JUST_SAVED_NAMED_REPORT}/:reportId/:campaignId?`,
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "AnalyticsPage" */
          "routes/Analytics/AnalyticsPage"
        )
    ),
  },
  [RouteNames.campaigns]: {
    path: "/campaigns",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "CampaignsPage" */
          "routes/Campaigns/CampaignsPage"
        )
    ),
  },
  [RouteNames.campaignsNew]: {
    path: "/campaigns/new",
    component: lazyWithRetry(
      () => import("routes/CampaignEdit/CampaignNewEntrypoint")
    ),
  },
  [RouteNames.campaignEdit]: {
    path: "/campaign/:campaignId/edit",
    component: lazyWithRetry(
      () => import("routes/CampaignEdit/CampaignEditEntrypoint")
    ),
  },
  [RouteNames.campaignCopy]: {
    path: "/campaign/:campaignId/copy",
    component: lazyWithRetry(
      () => import("routes/CampaignEdit/CampaignCopyEntrypoint")
    ),
  },
  [RouteNames.suggestedOptimisations]: {
    path: "/suggested-optimisations",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "SuggestedOptimisations" */
          "routes/SuggestedOptimisations/SuggestedOptimisations"
        )
    ),
  },
  [RouteNames.campaignView]: {
    path: "/campaign/:campaignId/:tabId?",
    allowedParams: { tabId: ["lineitems", "landings"] },
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "CampaignViewPage" */
          "routes/CampaignView/CampaignViewPage"
        )
    ),
  },
  [RouteNames.lineitemNew]: {
    path: "/campaigns/:campaignId/lineItem/new",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "LineItemNew" */
          "routes/CampaignView/LineItemEdit/LineItemNew"
        )
    ),
  },
  [RouteNames.lineitemEdit]: {
    path: "/campaign/:campaignId/lineItem/:lineItemId/edit",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "LineItemEdit" */
          "routes/CampaignView/LineItemEdit/LineItemEdit"
        )
    ),
  },
  [RouteNames.lineitemCopy]: {
    path: "/campaign/:campaignId/lineItem/:lineItemId/copy",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "LineItemCopy" */
          "routes/CampaignView/LineItemEdit/LineItemCopy"
        )
    ),
  },
  [RouteNames.landingNew]: {
    path: "/campaigns/:campaignId/landing/new",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "LandingNew" */
          "routes/CampaignView/Landings/LandingNew"
        )
    ),
  },
  [RouteNames.landingEdit]: {
    path: "/campaign/:campaignId/landing/:landingId/edit",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "LandingEdit" */
          "routes/CampaignView/Landings/LandingEdit"
        )
    ),
  },
  [RouteNames.landingCopy]: {
    path: "/campaign/:campaignId/landing/:landingId/copy",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "LandingCopy" */
          "routes/CampaignView/Landings/LandingCopy"
        )
    ),
  },
  [RouteNames.audiences]: {
    path: "/audiences",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "Audiences" */
          "routes/Audiences/AudiencesPage"
        )
    ),
  },
  [RouteNames.audienceNew]: {
    path: "/audiences/new",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "AudiencesNew" */
          "routes/Audiences/Edit/AudiencesNew"
        )
    ),
  },
  [RouteNames.traffic]: {
    path: "/traffic/:tableId?",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "Traffic" */
          "routes/TrafficRefactor/TrafficPage"
        )
    ),
  },
  [RouteNames.creatives]: {
    path: "/creatives",
    component: lazyWithRetry(
      () => import("routes/CreativesRedesign/CreativesRedesign")
    ),
    exact: false,
  },
  [RouteNames.creativeEditRedesign]: {
    path: "/creative/:creativeId/editRedesign",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "CreativeEditRedesign" */
          "routes/Creatives/CreativeRedesign/Edit/CreativeEdit"
        )
    ),
  },
  [RouteNames.billing]: {
    path: "/billing/:tabId",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "Billing" */
          "routes/Billing/components/container/BillingContainer"
        )
    ),
  },
  [RouteNames.billingNoTab]: {
    path: "/billing",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "Billing" */
          "routes/Billing/components/container/BillingContainer"
        )
    ),
  },
  [RouteNames.users]: {
    path: "/users",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "UserManagement" */
          "routes/Users/UserManagement"
        )
    ),
  },
  [RouteNames.userEdit]: {
    path: "/users/:userId/edit",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "UserEdit" */
          "routes/Users/UserEdit"
        )
    ),
  },
  [RouteNames.userNew]: {
    path: "/users/new",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "UserNew" */
          "routes/Users/UserNew"
        )
    ),
  },
  [RouteNames.organization]: {
    path: "/organization",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "Organization" */
          "routes/Organization/OrganizationPage"
        )
    ),
  },
  [RouteNames.adminSettings]: {
    path: "/admin-settings",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "AdminSettings" */
          "routes/AdminSettings/AdminSettingsPage"
        )
    ),
  },
  [RouteNames.appIntelligence]: {
    path: "/app-intelligence/:tabId?",
    exact: false,
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "AppIntelligence" */
          "routes/AppIntelligence/AppIntelligencePage"
        )
    ),
  },
  [RouteNames.datetimePickerTest]: {
    path: "/datetime-picker-test",
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "DatetimePickerTest" */
          "routes/Test/DatetimePickerTest"
        )
    ),
  },
  [RouteNames.notFound]: {
    exact: false,
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "NotFound" */
          "routes/PageNotFound"
        )
    ),
  },
  [RouteNames.testReporterQuery]: {
    exact: false,
    component: lazyWithRetry(
      () =>
        import(
          /* webpackChunkName: "TestReporterQuery" */
          "routes/Test/TestReporterQuery"
        )
    ),
  },
};
